import { forwardRef } from 'react';
import styled from '@emotion/styled';
import * as tokens from '@tokens';
import type { RadioInputProps } from './RadioInput';
import { RadioInput } from './RadioInput';
import type { BaseOptionInputProps } from '../BaseOptionInput';
import { BaseOptionInput } from '../BaseOptionInput';

export interface RadioProps
  extends RadioInputProps,
    Omit<BaseOptionInputProps, 'children'> {}

const StyledRadioInput = styled(RadioInput)`
  margin-right: ${tokens.spacer.x1};
`;

const StyledOptionInput = styled(BaseOptionInput)`
  width: fit-content;
`;

/**
 * Radio buttons let users choose a single option among two or more mutually exclusive options.
 *
 * {@link https://zeroheight.com/76c439891/v/latest/p/28cb3e-radio/b/17ea30 Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-radio--radio Code documentation}
 *
 *
 * @example
 * <Radio labelContent="My radio button" />
 */
export const Radio = forwardRef<HTMLInputElement, RadioProps>(
  (
    {
      isDisabled = false,
      labelHtmlProps = {},
      labelContent,
      helperText,
      className,
      ...props
    }: RadioProps,
    ref,
  ) => {
    return (
      <StyledOptionInput
        className={className}
        isDisabled={isDisabled}
        labelContent={labelContent}
        helperText={helperText}
        labelHtmlProps={labelHtmlProps}
      >
        <StyledRadioInput ref={ref} isDisabled={isDisabled} {...props} />
      </StyledOptionInput>
    );
  },
);

Radio.displayName = 'Radio';

Radio.defaultProps = {
  isInvalid: false,
  isRequired: false,
  isChecked: false,
};
